import React, { useState, useEffect } from 'react';
import moment from 'moment';
import Toggle from 'react-toggle';
import ReactTooltip from 'react-tooltip';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import isNull from 'lodash/isNull';
import isBoolean from 'lodash/isBoolean';
import styled, { css } from 'styled-components';
import classNames from 'classnames';
import Colors from '../../styles/colors';
import PricingAccordion from './PricingAccordion';
import { Table, Row, Cell } from 'react-sticky-table';
import { FlexiRow, BaseLandingButton, LandingBtnWrapper } from '../../styles/styled';
import useMobileDetect from '../../helpers/mobileDetect';
import { STATIC_HOMEPAGE_URL } from '../../config';
import { getAllEpisodesCountV2, getSoundcastsManagedCountV2 } from '../../helpers/userinfoHelper';
import parseQueryParams from '../../helpers/parseQueryParams';
import { couponsManager } from '../../helpers/managers';
const { getPlanLimits, getPublisherPlan, slugify } = require('../../../server/scripts/utils.js')();
import { getStartedPricingGTM, learnMorePricingGTM } from '../../utils/gtm';
import { ucfirst } from '../../utils/string';
import FEATURES_PLANS from './data';
const { PLANS, FEATURES } = FEATURES_PLANS;

const Pricing = props => {
  const { userInfo } = props;
  const [frequency, setFrequency] = useState('annual');
  const [publisherPlan, setPublisherPlan] = useState({});
  const [discount, setDiscount] = useState(0);
  const [partnerPlan, setPartnerPlan] = useState('');
  const isMobile = useMobileDetect();

  const queryString = window.location.search;
  const { discount: discountCode = '' } = parseQueryParams(queryString);

  useEffect(() => {
    const npublisherPlan = getPublisherPlan(userInfo.publisher);
    const { active = false, frequency: pfrequency = '', isPartner = false } = npublisherPlan;
    if (active) {
      setPublisherPlan(npublisherPlan);
      // pfrequency comes undefined if user register without cc
      const _pfrequency = !pfrequency || pfrequency === 'trail' ? 'monthly' : pfrequency;
      setFrequency(isPartner ? 'annual' : _pfrequency);
    }
  }, [userInfo.publisher]);

  useEffect(() => {
    if (!isEmpty(discountCode)) {
      couponsManager.getByCode(discountCode).then(coupon => {
        const { percentOff = 0, expiration = null, partnerPlan: pplan = '' } = coupon;
        if (!isNull(expiration) && percentOff > 0 && expiration > moment().unix()) {
          setDiscount(percentOff / 100);
        }

        if (!isEmpty(pplan)) {
          setPartnerPlan(pplan);
        }
      });
    }
  }, [discountCode]);

  const isCurrentAndActivePlan = planType => {
    // frequency default to annual (for partner accounts)
    const {
      active = false,
      plan = '',
      frequency: pfrequency = '',
      isPartner = false,
    } = publisherPlan;

    // partner should always return the active state
    const matchedFrequency = isPartner ? true : isEqual(frequency, pfrequency);
    const conditions = [active, isEqual(planType, plan.toLowerCase()), matchedFrequency];
    return conditions.every(value => !!value);
  };

  const changeFrequency = () => {
    const value = frequency === 'annual' ? 'monthly' : 'annual';
    setFrequency(value);
  };

  const getFrequencyPrice = (price, planName = '') => {
    const isPartnerPlan = isEqual(planName.toLowerCase(), partnerPlan.toLowerCase());
    if (discount <= 0 || isPartnerPlan) return price;
    const discountPrice = discount * price;
    return Number(price - discountPrice).toFixed(2);
  };

  const getFormattedValue = price => {
    return Math.round(price) !== price ? Number(price).toFixed(2) : price;
  };

  const checkoutRow = (planType, setTestId) => {
    const buttonProps = {
      onClick: () => {
        getStartedPricingGTM({ type: ucfirst(planType) + '_' + ucfirst(frequency) });
        // verify plan limits: when downgrading from higher plans, make sure the current soundcast and episodes count
        // stays in the level of the plan a user is downgrading to
        const { soundcasts: soundcastLimit, episodes: episodesLimit } = getPlanLimits(
          planType,
          true
        );
        const { active } = getPublisherPlan(userInfo.publisher);
        if (active) {
          const soundcastsCount = getSoundcastsManagedCountV2();
          const episodesCount = getAllEpisodesCountV2();
          const canChangePlan = [
            soundcastsCount <= soundcastLimit,
            episodesCount <= episodesLimit,
          ].every(value => !!value);

          if (!canChangePlan) {
            return props.showAlertModal({
              content:
                'The volume of audios you currently have exceeds the limit for this plan. Please contact support at support@mysoundwise.com',
            });
          }
        }

        props.goToCheckout(planType, frequency, discountCode);
      },
    };

    // show current plan
    const checkOutLabel = isCurrentAndActivePlan(planType) ? 'Current Plan' : 'Get Started';

    // button props
    buttonProps.id = setTestId ? 'pricingGetPlusBtnTest' : '';
    buttonProps.disabled = isCurrentAndActivePlan(planType);

    const btnClassName = classNames('alt-font-2 font-weight-100 xs-title-small', {
      'text-large': !isMobile,
      'width-70 margin-bottom-20px': isMobile,
      'active-plan': isCurrentAndActivePlan(planType),
    });

    return (
      <GetStarted inverted="true" className={btnClassName} {...buttonProps}>
        {checkOutLabel}
      </GetStarted>
    );
  };

  const renderCellsForPlan = () => {
    return PLANS.map(plan => {
      const { id, name, price, highlight = false } = plan;
      const CellComponent = highlight ? HeaderCellPlan : Cell;
      const annualSave = getFrequencyPrice(price.monthly - price.annual, name) * 12;
      const frequencyPrice = getFrequencyPrice(price[frequency], name);
      const savePrice =
        frequency === 'annual' ? `Save $${getFormattedValue(annualSave)} a year` : '';
      const isPartnerPlan = isEqual(name.toLowerCase(), partnerPlan.toLowerCase());
      const discounted = !isPartnerPlan && discount > 0;

      const learnMorePlatinumLink = `${STATIC_HOMEPAGE_URL}/blog/5-reasons-to-create-your-own-white-label-audio-app-with-soundwise-platinum`;
      const isPlatinum = id === 'platinum';

      if (isMobile) {
        return (
          <PlanMetadataMobile key={`pricing-plan-${id}`} className="row centered">
            {highlight && (
              <PopularPlanRibbonMobile className="ribbon-top-right">
                <span>Most Popular</span>
              </PopularPlanRibbonMobile>
            )}
            <h1 className="text-center font-weight-600 xs-title-extra-large-2 alt-font-2 xs-margin-two-top margin-bottom-20px">
              {name}
            </h1>
            {checkoutRow(id, '#FF6F00', id === 'starter')}
            {isPlatinum && (
              <LearnMorePlatinumLink
                onClick={() => {
                  learnMorePricingGTM();
                }}
                href={`${learnMorePlatinumLink}`}
                target="blank">
                Learn more
              </LearnMorePlatinumLink>
            )}
            {!isPlatinum && <PlanDivider className="margin-top-20px margin-bottom-20px" />}
            {discounted && (
              <PlanPrice alignItems="flex-start">
                <CrossOutPrice>
                  <span className="rawprice title-large font-weight-400 alt-font-2">
                    ${price[frequency]}
                  </span>
                </CrossOutPrice>
              </PlanPrice>
            )}
            {!isPartnerPlan && (
              <>
                <PlanPrice alignItems="center" className="margin-bottom-20px">
                  <span className="currency xs-title-extra-large">$</span>
                  <span className="rawprice xs-title-extra-large-4 font-weight-600 alt-font-2">
                    {getFormattedValue(frequencyPrice)}
                  </span>
                  <span className="frequency xs-title-small alt-font-2">/mo</span>
                </PlanPrice>
                {!isEmpty(savePrice) && (
                  <div className="xs-text-large font-weight-100 margin-bottom-10px">
                    {savePrice}
                  </div>
                )}
              </>
            )}
          </PlanMetadataMobile>
        );
      }

      return (
        <CellComponent key={`pricing-plan-${id}`} className="padding-20px">
          <PlanMetadata className="padding-top-10px">
            {highlight && (
              <PopularPlanRibbon>
                <span>Most Popular</span>
              </PopularPlanRibbon>
            )}
            <h1 className="title-extra-large font-weight-400 alt-font-2 margin-bottom-20px">
              {name}
            </h1>
            {checkoutRow(id, '#FF6F00', id === 'starter')}
            {isPlatinum && (
              <LearnMorePlatinumLink
                onClick={() => {
                  learnMorePricingGTM();
                }}
                href={`${learnMorePlatinumLink}`}
                target="blank">
                Learn more
              </LearnMorePlatinumLink>
            )}
            {!isPlatinum && <PlanDivider className="margin-top-20px margin-bottom-20px" />}
            {discounted && (
              <PlanPrice alignItems="flex-start">
                <CrossOutPrice>
                  <span className="rawprice title-large font-weight-400 alt-font-2">
                    ${price[frequency]}
                  </span>
                </CrossOutPrice>
              </PlanPrice>
            )}
            {!isPartnerPlan && (
              <>
                <PlanPrice alignItems="flex-start" className="margin-bottom-20px">
                  <span className="currency title-large">$</span>
                  <span className="rawprice title-extra-large font-weight-400 alt-font-2">
                    {getFormattedValue(frequencyPrice)}
                  </span>
                  <span className="frequency title-small font-weight-400 alt-font-2">/mo</span>
                </PlanPrice>
                <div className="text-large font-weight-100 margin-bottom-10px">{savePrice}</div>
              </>
            )}
          </PlanMetadata>
        </CellComponent>
      );
    });
  };

  const parseFeatureValue = value => {
    if (isBoolean(value)) {
      return value ? <FeatureIcon className="far fa-check-circle" /> : null;
    }

    return value;
  };

  const getCellClassValue = value => {
    return classNames(
      'feature-text text-center text-dark-gray alt-font-2 title-small padding-fourteen',
      {
        'only-opacity-light': !isBoolean(value),
      }
    );
  };

  const renderCellsForFeatures = () => {
    const highlightColumId = PLANS.findIndex(item => item.highlight);
    return FEATURES.map((feature, i) => {
      const { name, value, promo = [], tooltip = '' } = feature;
      const id = slugify(name);
      const featureValue = !isEmpty(promo) ? promo : value;
      const lastRow = i === FEATURES.length - 1;

      if (isMobile) {
        return (
          <PlanFeatureMobile key={`${slugify}-${i}`}>
            <div className="panel panel-default">
              <div className="panel-heading">
                <FlexiRow
                  alignItems="center"
                  justifyContent="space-between"
                  className="panel-title">
                  {name}
                  {!isEmpty(tooltip) && (
                    <ToolTipWrapper>
                      <TipIcon data-for={id} data-tip={tooltip.replace(/\n/g, '<br/>')}>
                        <span className="material-icons">info_outline</span>
                      </TipIcon>
                      <ReactTooltip
                        id={id}
                        effect="solid"
                        place="left"
                        type="dark"
                        multiline={true}
                      />
                    </ToolTipWrapper>
                  )}
                </FlexiRow>
              </div>
              <ul className="list-group">
                {featureValue.map((planValue, i) => {
                  return (
                    <li key={`plan-value-${i}`} className="list-group-item">
                      <FlexiRow alignItems="center" justifyContent="space-between">
                        <span className="text-large alt-font-2 font-weight-400">
                          {PLANS[i].name}
                        </span>
                        <span className="text-large alt-font-2 font-weight-400 pull-right">
                          {parseFeatureValue(planValue)}
                        </span>
                      </FlexiRow>
                    </li>
                  );
                })}
              </ul>
            </div>
          </PlanFeatureMobile>
        );
      }

      return (
        <Row key={`${slugify}-${i}`}>
          <Cell>
            <FlexiRow alignItems="center" className="feature-text">
              <span className="only-opacity-light text-dark-gray alt-font-2 title-small">
                {name}
              </span>
              {!isEmpty(tooltip) && (
                <ToolTipWrapper>
                  <TipIcon data-for={id} data-tip={tooltip.replace(/\n/g, '<br/>')}>
                    <span className="material-icons">info_outline</span>
                  </TipIcon>
                  <ReactTooltip
                    id={id}
                    effect="solid"
                    place="right"
                    type="dark"
                    multiline={true}
                    className="line-height-20"
                  />
                </ToolTipWrapper>
              )}
            </FlexiRow>
          </Cell>
          {featureValue.map((value, index) => {
            const isCellHighlight = index === highlightColumId;
            const isCellHighlightLast = lastRow && isCellHighlight;
            const CellComponent = isCellHighlight ? CellHighlight : Cell;
            return (
              <CellComponent key={`cell-value-${i}-${index}`}>
                <div className={getCellClassValue(value)}>{parseFeatureValue(value)}</div>
                { isCellHighlightLast && <CellHighlightLast/> }
              </CellComponent>
            );
          })}
        </Row>
      );
    });
  };

  const renderPricingView = () => {
    if (isMobile) {
      return (
        <>
          <div>{renderCellsForPlan()}</div>
          <div>{renderCellsForFeatures()}</div>
        </>
      );
    }

    return (
      <Table>
        <Row>
          <Cell />
          {renderCellsForPlan()}
        </Row>
        <Row>
          <HeaderCell>
            <div className="feature-header text-large text-black alt-font-2 title-small font-weight-700">
              Plan Feature
            </div>
          </HeaderCell>
          {PLANS.map((plan, index) => {
            const HeaderCellComponent = plan.highlight ? HeaderCellHighlight : HeaderCell;
            return <HeaderCellComponent key={`cell-header-${index}`} className="feature-header" />;
          })}
        </Row>
        {renderCellsForFeatures()}
      </Table>
    );
  };

  const getFrequencyToggleClass = (cfrequency, custom = '') =>
    classNames('title-medium xs-title-medium font-weight-400 alt-font-2', custom, {
      'text-light-gray': cfrequency !== frequency,
    });

  return (
    <PricingWrapper>
      <section className="padding-60px-top padding-60px-bottom xs-padding-bottom-20px offer bg-white">
        <div className="container">
          <div className="row">
            <div className="col-md-12 col-sm-12 col-xs-12 text-center margin-four-bottom">
              <h1 className="alt-font-2 font-weight-400 title-extra-large-2 xs-title-extra-large-4">
                The best way to sell audio products online
              </h1>
            </div>
          </div>
          <FlexiRow alignItems="center" justifyContent="center">
            <span
              className={getFrequencyToggleClass(
                'monthly',
                'padding-two-right sm-padding-six-right'
              )}>
              Monthly
            </span>
            <ToggleFrequency
              id="frequency"
              icons={false}
              checked={frequency === 'annual'}
              onChange={changeFrequency}
            />
            <span
              className={getFrequencyToggleClass('annual', 'padding-two-left sm-padding-six-left')}>
              Annual
            </span>
          </FlexiRow>
        </div>
      </section>

      <section className="bg-white">
        <div className="container">
          <div className="row">{renderPricingView()}</div>
        </div>
      </section>

      <section className="padding-70px-tb xs-padding-20px-tb">
        <div className="container">
          <div className="row">
            <PricingAccordion />
          </div>
        </div>
      </section>

      <SectionBottom
        className="padding-60px-tb offer bg-white builder-bg xs-padding-60px-tb"
        id="callto-action5"
        data-selector=".builder-bg">
        <div className="container">
          <div className="row">
            <div className="col-md-12 col-sm-12 col-xs-12 text-center">
              <div className="offer-box-left">
                <span
                  className="title-small xs-title-extra-large display-block tz-text text-deep-gray alt-font-2 title-extra-large"
                  data-selector=".tz-text">
                  More questions? Check out our&nbsp;
                  <PriceLink
                    className="alt-font-2 xs-title-extra-large"
                    href={`${STATIC_HOMEPAGE_URL}/faqs`}
                    target="blank">
                    Publisher FAQs
                  </PriceLink>
                  <Divider>
                    <p className="divider">
                      <span>or</span>
                    </p>
                  </Divider>
                </span>
                <ContactUsBtnWrappr>
                  <ContactUs
                    href={`${STATIC_HOMEPAGE_URL}/contact-us`}
                    target="_blank"
                    inverted="true"
                    className="font-weight-100 text-center text-large sm-text-large xs-text-large margin-top-10px">
                    CONTACT US
                  </ContactUs>
                </ContactUsBtnWrappr>
              </div>
            </div>
          </div>
        </div>
      </SectionBottom>
    </PricingWrapper>
  );
};

const SectionBottom = styled.section`
  background-color: #f2f2f2;
`;

const PricingWrapper = styled.div`
  .checkout-label {
    &:not(.disabled) {
      color: #fff;
    }
  }

  .container {
    @media (min-width: 1200px) {
      width: 1280px;
    }
  }

  ${Table} {
    overflow: visible;
    box-shadow: 0px 3px 8px 0px rgba(0, 0, 0, 0.1);
    width: 100%;

    ${Cell} {
      border-right: 0.7px solid rgba(112, 112, 112, 0.1);
      border-bottom: 0.7px solid rgba(112, 112, 112, 0.1);
      padding: 0;

      .feature-header {
        padding: 20px 35px;
      }

      .feature-text {
        padding: 16px 35px;
      }
    }
  }
`;

const PlanDivider = styled.div`
  width: 40%;
  height: 7px;
  border-radius: 3px;
`;

const PlanMetadata = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  position: relative;

  ${PlanDivider} {
    background: #f2f2f2;
  }
`;

const PopularPlanRibbon = styled.div`
  position: absolute;
  width: 100%;
  text-align: right;
  right: -14px;
  top: -10px;

  span {
    color: ${Colors.mainWhite};
    text-transform: uppercase;
  }

  &:after {
    content: '';
    display: block;
    border: 17px solid ${Colors.mainOrange};
    border-left-color: ${Colors.mainWhite};
    background-color: ${Colors.mainOrange};
    z-index: -1;
    width: 80%;
    position: absolute;
    top: -5px;
    right: -10px;
    border-top-right-radius: 4px;
    border-bottom-right-radius: 4px;
  }
`;

const PopularPlanRibbonMobile = styled.div`
  width: 105px;
  height: 105px;
  overflow: hidden;
  position: absolute;

  span {
    position: absolute;
    display: block;
    width: 225px;
    padding: 5px 0;
    background-color: ${Colors.mainOrange};
    box-shadow: 0 5px 10px rgba(0, 0, 0, 0.1);
    color: #fff;
    text-shadow: 0 1px 1px rgba(0, 0, 0, 0.2);
    text-transform: uppercase;
    text-align: center;
    font-size: 1.1rem;
  }

  &:before,
  &:after {
    position: absolute;
    content: '';
    display: block;
    border: 5px solid ${Colors.mainOrange};
  }

  &.ribbon-top-right {
    top: -10px;
    right: -10px;

    &:before,
    &:after {
      border-top-color: transparent;
      border-right-color: transparent;
    }

    &:before {
      top: 0;
      left: 0;
    }

    &:after {
      bottom: 0;
      right: 0;
    }

    span {
      left: -50px;
      top: 25px;
      transform: rotate(45deg);
    }
  }
`;

const PlanMetadataMobile = styled(FlexiRow)`
  align-items: center;
  flex-direction: column;
  border: 1px solid #dfe1e5;
  padding: 20px;
  margin: 20px;
  border-radius: 4px;
  position: relative;
`;

const PlanPrice = styled(FlexiRow)`
  .currency {
    margin-top: 2px;

    @media (max-width: 767px) {
      margin-top: -12px;
    }
  }

  .frequency {
    align-self: flex-end;

    @media (max-width: 767px) {
      margin-bottom: 2px;
    }
  }
`;

const PlanFeatureMobile = styled.div`
  margin: 20px;

  .panel {
    border: 1px solid #ddd;
  }

  .list-group-item {
    padding: 10px 18px;
  }
`;

const ToolTipWrapper = styled.div`
  margin-left: 10px;

  & > span {
    display: block;
    line-height: 0px;
    opacity: 0.65;
  }
`;

const TipIcon = styled.span`
  .fa,
  .material-icons {
    cursor: pointer;
  }

  .fa {
    margin-bottom: 3px;
    opacity: 0.4;
    font-size: 20px;
  }
`;

const GetStarted = styled.button`
  ${BaseLandingButton}
  border-radius: 5px;
  color: ${Colors.mainWhite};
  padding: 10px 41px;
  width: 180px;
  height: 47px;
  border: 1px solid ${Colors.mainOrange};
  border-width: thin;

  @media (max-width: 767px) {
    padding: 10px 50px;
  }

  &:hover {
    &:not(.active-plan) {
      opacity: 0.7;
      transform: translateY(-1px);
    }
  }

  &.active-plan {
    transition: none;
    animation: none;
    border-color: transparent;
    background: ${Colors.mainWhite};
    color: ${Colors.mainOrange};
  }
`;

const LandingButton = styled.a`
  ${BaseLandingButton}
`;

const ContactUs = styled(LandingButton)`
  border-radius: 5px;
`;

const ContactUsBtnWrappr = styled(LandingBtnWrapper)``;

const Divider = styled.div`
  font: normal normal normal 20px/26px Roboto;
  padding: 0 18%;
  .divider {
    align-text: center;
    border-bottom: 1px solid #000;
    line-height: 0.1em;
    margin: 20px 0 20px 0;
    color: #222222;
    opacity: 0.25;

    span {
      background: #f2f2f2;
      padding: 0 10px;
    }
  }
`;

const PriceLink = styled.a`
  text-decoration: underline;
  color: ${Colors.mainOrange};
  font-size: 35px;

  &:hover {
    color: ${Colors.mainOrange};
  }
`;

const LearnMorePlatinumLink = styled.a`
  text-decoration: underline;
  color: #222222;
  font-size: 14px;
  width: 100%;
  margin-top: 12px;
  margin-bottom: 12px;
  text-align: center;
  opacity: 0.75;
  font-family: 'Roboto' !important;
  ${props => (props.hidden ? 'visibility: hidden;' : '')}

  &:visited {
    color: #222222;
  }
`;

const ToggleFrequency = styled(Toggle)`
  height: 100%;

  &.react-toggle--checked {
    .react-toggle-track {
      background-color: #ff6f00;

      * {
        display: none;
      }
    }

    .react-toggle-thumb {
      border-color: #ff6f00;
    }

    &:hover {
      &:not(.react-toggle--disabled) .react-toggle-track {
        background-color: #ff6f00;
      }
    }
  }
`;

const HeaderCell = styled(Cell)`
  &&& {
    background: #f2f2f2;
    border-bottom: none;
  }
`;

const Highlight = css`
  position: relative;
  z-index: 3;
  box-shadow: 0px 6px 5px 0px rgba(0, 0, 0, 0.3);
  &:before {
    content: '';
    position: absolute;
    border: 2px solid #ff6f00;
    width: calc(100% + 1px);
    left: 0px;
    height: calc(100% + 2px);
    top: -1px;
    border-top: none;
    border-bottom: none;
    z-index: 2;
  }
`;

const CellHighlight = styled(Cell)`
  ${Highlight}
`;

const CellHighlightLast = styled(CellHighlight)`
  position: absolute;
  width: calc(100% + 1px);
  &:before {
    background-color: #fff;
    top: calc(100%);
    height: 15px;
    border-top: none;
    border-bottom: 2px solid #ff6f00;
    border-radius: 0 0 5px 5px;
  }
`;

const HeaderCellHighlight = styled(HeaderCell)`
  ${Highlight}
  box-shadow: 0px 2px 3px 0px rgba(112, 112, 112, 0.1);
  &:before {
    box-shadow: 0px 2px 3px 0px rgba(112, 112, 112, 0.1);
  }
`;

const HeaderCellPlan = styled(Cell)`
  position: relative !important;
  border: none;
  z-index: 3 !important;
  box-shadow: 0px 2px 3px 0px rgba(0, 0, 0, 0.1);
  &:before {
    content: '';
    position: absolute;
    background-color: #fff;
    border: 2px solid #ff6f00;
    width: calc(100% + 1px);
    left: 0px;
    height: calc(100% + 16px);
    top: -15px;
    border-bottom: none;
    box-shadow: 0px 2px 3px 0px rgba(0, 0, 0, 0.1);
    border-radius: 5px 5px 0 0;
    z-index: -1;
  }
`;

const FeatureIcon = styled.i`
  color: #32ba7c;
  font-size: 20px;
  font-weight: bold;
  padding: 3px 0px;
`;

const CrossOutPrice = styled.div`
  display: flex;
  align-self: center;
  text-decoration: line-through;
  color: #f44336;
  margin-right: 5px;
`;

export default Pricing;
